import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  ArrowLeft,
  Crown,
  Check,
  Sparkle,
  GraduationCap,
  Lightning,
  Shield,
  Gift,
  CircleNotch,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { useQuery, useAction, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { getCurrentLanguage } from "@shared/lib/i18n";
import { PaywallTemplate } from "@shared/components/PaywallTemplate";
import { Template1Paywall } from "@shared/components/Template1Paywall";
import { Template2Paywall } from "@shared/components/Template2Paywall";
import { Template3Paywall } from "@shared/components/Template3Paywall";
import { USER_SESSION_KEY } from "@shared/hooks/useUserSession";

export const Route = createFileRoute("/app/membership")({
  component: MembershipPage,
});

type Plan = "student" | "standard";

interface PlanDetails {
  id: Plan;
  name: { ro: string; en: string };
  price: number;
  duration: { ro: string; en: string };
  icon: React.ReactNode;
  features: { ro: string; en: string }[];
  popular?: boolean;
  color: string;
}

// Plans will be populated with dynamic pricing from settings
const getPlans = (pricing: { student: { price: number; durationMonths: number }; standard: { price: number; durationMonths: number } }): PlanDetails[] => [
  {
    id: "student",
    name: { ro: "Student", en: "Student" },
    price: pricing.student.price,
    duration: { 
      ro: pricing.student.durationMonths === 1 ? "1 lună" : `${pricing.student.durationMonths} luni`,
      en: pricing.student.durationMonths === 1 ? "1 month" : `${pricing.student.durationMonths} months`
    },
    icon: <GraduationCap size={24} />,
    color: "from-blue-500 to-indigo-600",
    features: [
      { ro: "Reduceri la restaurante", en: "Restaurant discounts" },
      { ro: "Acces la parteneri de mâncare", en: "Food partner access" },
      { ro: "5 invitații pentru prieteni", en: "5 invites for friends" },
      { ro: "Suport prioritar", en: "Priority support" },
    ],
  },
  {
    id: "standard",
    name: { ro: "Standard", en: "Standard" },
    price: pricing.standard.price,
    duration: { 
      ro: pricing.standard.durationMonths === 1 ? "1 lună" : `${pricing.standard.durationMonths} luni`,
      en: pricing.standard.durationMonths === 1 ? "1 month" : `${pricing.standard.durationMonths} months`
    },
    icon: <Crown size={24} />,
    color: "from-primary to-accent-dark",
    popular: true,
    features: [
      { ro: "Acces la toate locațiile", en: "Access to all locations" },
      { ro: "Reduceri nelimitate", en: "Unlimited discounts" },
      { ro: "10 invitații pentru prieteni", en: "10 invites for friends" },
      { ro: "Oferte exclusive", en: "Exclusive offers" },
      { ro: "Suport prioritar", en: "Priority support" },
    ],
  },
];

function MembershipPage() {
  const navigate = useNavigate();
  const currentLang = getCurrentLanguage();
  const [userId, setUserId] = useState<Id<"users"> | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<Plan>("standard");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCheckout = useAction(api.stripe.createCheckoutSession);
  const trackPaywallView = useMutation(api.paywall.trackPaywallView);
  const trackPaywallConversion = useMutation(api.paywall.trackPaywallConversion);

  const [sessionToken, setSessionToken] = useState<string | null>(null);

  useEffect(() => {
    const storedUserId = localStorage.getItem("userId");
    setSessionToken(localStorage.getItem(USER_SESSION_KEY));
    if (storedUserId) {
      setUserId(storedUserId as Id<"users">);
    } else {
      navigate({ to: "/auth/login" });
    }
  }, [navigate]);

  const user = useQuery(api.auth.getCurrentUser, userId ? { userId } : "skip");
  const paywallTemplate = useQuery(
    api.paywall.getUserPaywallTemplate,
    userId && sessionToken ? { userId, sessionToken } : "skip"
  );
  const pricing = useQuery(api.paywall.getPricing);

  // Track paywall view when template is loaded
  useEffect(() => {
    if (paywallTemplate && userId) {
      trackPaywallView({
        userId,
        templateId: paywallTemplate._id,
      }).catch(console.error);

      // Track in PostHog
      import("@shared/lib/posthog-lazy").then(({ trackEvent }) => {
        trackEvent("paywall_viewed", {
          template_id: paywallTemplate._id,
          template_name: paywallTemplate.name,
        });
      });
    }
  }, [paywallTemplate, userId, trackPaywallView]);

  const handleSubscribe = async () => {
    if (!userId) return;

    setIsLoading(true);
    setError(null);

    // Track subscribe click
    if (paywallTemplate) {
      import("@shared/lib/posthog-lazy").then(({ trackEvent }) => {
        trackEvent("paywall_subscribe_clicked", {
          template_id: paywallTemplate._id,
          template_name: paywallTemplate.name,
          plan: selectedPlan,
        });
      });
    }

    try {
      const result = await createCheckout({
        userId,
        sessionToken: sessionToken!,
        plan: selectedPlan,
      });

      if (result.success && result.url) {
        // Track conversion before redirect
        if (paywallTemplate && userId) {
          await trackPaywallConversion({
            userId,
            planSelected: selectedPlan,
          });

          import("@shared/lib/posthog-lazy").then(({ trackEvent }) => {
            trackEvent("paywall_converted", {
              template_id: paywallTemplate._id,
              template_name: paywallTemplate.name,
              plan: selectedPlan,
            });
          });
        }

        // Redirect to Stripe Checkout
        window.location.href = result.url;
      } else if (!result.success) {
        setError(result.error || "Failed to create checkout session");
      }
    } catch (err) {
      setError(
        currentLang === "ro"
          ? "A apărut o eroare. Încearcă din nou."
          : "An error occurred. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!userId || user === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (user === null) {
    localStorage.removeItem("userId");
    navigate({ to: "/auth/login" });
    return null;
  }

  const isActive = user.membershipStatus === "active";

  // Get plans with dynamic pricing
  const plans = pricing ? getPlans(pricing) : getPlans({ student: { price: 29, durationMonths: 1 }, standard: { price: 49, durationMonths: 1 } });

  // Determine which template component to render
  const renderPaywallTemplate = () => {
    if (!paywallTemplate) {
      return null;
    }

    const templateName = paywallTemplate.name;

    // Hardcoded templates
    if (templateName === "Template1") {
      return (
        <Template1Paywall
          plans={plans}
          currentLang={currentLang}
          selectedPlan={selectedPlan}
          onPlanSelect={setSelectedPlan}
          onSubscribe={handleSubscribe}
          isLoading={isLoading}
          isActive={isActive}
        />
      );
    }

    if (templateName === "Template2") {
      return (
        <Template2Paywall
          plans={plans}
          currentLang={currentLang}
          selectedPlan={selectedPlan}
          onPlanSelect={setSelectedPlan}
          onSubscribe={handleSubscribe}
          isLoading={isLoading}
          isActive={isActive}
        />
      );
    }

    if (templateName === "Template3") {
      return (
        <Template3Paywall
          plans={plans}
          currentLang={currentLang}
          selectedPlan={selectedPlan}
          onPlanSelect={setSelectedPlan}
          onSubscribe={handleSubscribe}
          isLoading={isLoading}
          isActive={isActive}
        />
      );
    }

    // Fallback to dynamic template (if exists)
    // For now, return null and use fallback design
    return null;
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-b from-background to-gray-100 overflow-hidden">
      {/* Header fix - compact */}
      <header className="bg-primary text-white px-4 pt-6 pb-4 rounded-b-3xl flex-shrink-0 z-40 safe-area-top-padding">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Link to="/app/profile" search={{ success: false, canceled: false, session_id: undefined }} className="p-2 bg-white/20 rounded-lg -ml-2">
              <ArrowLeft size={20} />
            </Link>
            <img 
              src="/suuper_logo_white.svg" 
              alt="Suuper" 
              className="h-8 w-auto"
            />
            <p className="text-primary  text-2xl font-semibold">
              {currentLang === "ro" ? "Abonament" : "Subscription"}
            </p>
          </div>
        </div>

        <div className="text-center mb-3">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
            <Sparkle size={24} />
          </div>
          <h5 className="text-xl font-bold mb-1">
            {currentLang === "ro"
              ? "Alege planul potrivit"
              : "Choose the right plan"}
          </h5>
          <p className="text-white/80 text-xs">
            {currentLang === "ro"
              ? "Acces nelimitat la reduceri exclusive"
              : "Unlimited access to exclusive discounts"}
          </p>
        </div>

        {/* Search bar - compact */}
        <Link
          to="/app/offers"
          search={{ focusSearch: true }}
          className="relative block"
        >
          <MagnifyingGlass className="absolute left-2 top-1/2 transform -translate-y-1/2" size={16} color="rgba(255,255,255,0.7)" />
          <div className="w-full pl-8 pr-3 py-1.5 border border-white/20 rounded-lg bg-white/10 text-white/70 text-xs cursor-pointer backdrop-blur">
            {currentLang === "ro" ? "Caută oferte..." : "Search offers..."}
          </div>
        </Link>
      </header>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto pb-20">
        {/* Current status banner - compact */}
      {isActive && (
        <div className="mx-4 -mt-4 mb-3 bg-success/10 border border-success/30 rounded-xl p-3 flex items-center gap-2">
          <div className="w-8 h-8 bg-success/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Check size={16} className="text-success" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-success text-sm">
              {currentLang === "ro" ? "Abonament activ" : "Active membership"}
            </p>
            <p className="text-xs text-text-muted truncate">
              {currentLang === "ro"
                ? `Plan ${user.membershipType}`
                : `${user.membershipType} plan`}
            </p>
          </div>
        </div>
      )}

      {/* Paywall Content */}
      <div className="px-4 -mt-2">
        {paywallTemplate ? (
          // Render hardcoded or dynamic template
          renderPaywallTemplate() || (
            // Fallback to dynamic template if it's not a hardcoded one
            <PaywallTemplate
              template={paywallTemplate as any}
              plans={plans}
              currentLang={currentLang}
              selectedPlan={selectedPlan}
              onPlanSelect={setSelectedPlan}
              onSubscribe={handleSubscribe}
              isLoading={isLoading}
              isActive={isActive}
            />
          )
        ) : (
          // Fallback to default design
          <>
            {/* Plans - 2 columns */}
            <div className="grid grid-cols-2 gap-2">
              {plans.map((plan) => (
                <button
                  key={plan.id}
                  onClick={() => setSelectedPlan(plan.id)}
                  className={`text-left relative overflow-hidden rounded-xl transition-all duration-300 ${
                    selectedPlan === plan.id
                      ? "ring-2 ring-primary ring-offset-1 scale-[1.02]"
                      : "hover:scale-[1.01]"
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute top-0 right-0 bg-primary text-white text-[10px] font-bold px-2 py-0.5 rounded-bl-lg z-10">
                      {currentLang === "ro" ? "POPULAR" : "POPULAR"}
                    </div>
                  )}

                  <div className="bg-white p-3 shadow-sm h-full">
                    <div className="flex flex-col">
                      <div className="flex items-start gap-2 mb-2">
                        <div
                          className={`w-10 h-10 rounded-lg bg-gradient-to-br ${plan.color} flex items-center justify-center text-white flex-shrink-0`}
                        >
                          {plan.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-sm text-text truncate">
                            {plan.name[currentLang as "ro" | "en"]}
                          </h3>
                          <p className="text-xs text-text-muted">
                            {plan.duration[currentLang as "ro" | "en"]}
                          </p>
                        </div>
                      </div>

                      <div className="mb-2">
                        <div className="text-right">
                          <span className="text-xl font-bold text-text">
                            {plan.price}
                          </span>
                          <span className="text-text-muted text-xs"> RON</span>
                        </div>
                      </div>

                      <div className="space-y-1">
                        {plan.features.slice(0, 3).map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-1.5">
                            <Check size={12} className="text-success flex-shrink-0 mt-0.5" />
                            <span className="text-[11px] text-text leading-tight">
                              {feature[currentLang as "ro" | "en"]}
                            </span>
                          </div>
                        ))}
                        {plan.features.length > 3 && (
                          <div className="flex items-start gap-1.5">
                            <Check size={12} className="text-success flex-shrink-0 mt-0.5" />
                            <span className="text-[11px] text-text leading-tight">
                              +{plan.features.length - 3} {currentLang === "ro" ? "mai multe" : "more"}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Selection indicator */}
                    <div
                      className={`absolute left-0 top-0 bottom-0 w-1 transition-colors ${
                        selectedPlan === plan.id ? "bg-primary" : "bg-transparent"
                      }`}
                    />
                  </div>
                </button>
              ))}
            </div>

            {/* Benefits section - compact */}
            <div className="mt-3">
              <h3 className="font-semibold text-text text-sm mb-2">
                {currentLang === "ro" ? "De ce e bun Suuper?" : "Why is Suuper good?"}
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <Lightning size={20} className="text-primary mb-1" />
                  <p className="text-xs font-medium text-text">
                    {currentLang === "ro" ? "Reduceri instant" : "Instant discounts"}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <Shield size={20} className="text-primary mb-1" />
                  <p className="text-xs font-medium text-text">
                    {currentLang === "ro" ? "Plată securizată" : "Secure payment"}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <Gift size={20} className="text-primary mb-1" />
                  <p className="text-xs font-medium text-text">
                    {currentLang === "ro" ? "Oferte exclusive" : "Exclusive offers"}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm">
                  <Crown className="w-5 h-5 text-primary mb-1" />
                  <p className="text-xs font-medium text-text">
                    {currentLang === "ro" ? "Acces premium" : "Premium access"}
                  </p>
                </div>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="mt-3 p-3 bg-error/10 border border-error/30 rounded-xl">
                <p className="text-error text-xs">{error}</p>
              </div>
            )}

            {/* Subscribe button - compact */}
            <div className="mt-3">
              <button
                onClick={handleSubscribe}
                disabled={isLoading}
                className="w-full py-3 bg-primary text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors text-sm"
              >
                {isLoading ? (
                  <>
                    <CircleNotch size={18} className="animate-spin" />
                    {currentLang === "ro" ? "Se procesează..." : "Processing..."}
                  </>
                ) : (
                  <>
                    <Crown size={18} />
                    {currentLang === "ro"
                      ? `Abonează-te - ${plans.find((p) => p.id === selectedPlan)?.price} RON`
                      : `Subscribe - ${plans.find((p) => p.id === selectedPlan)?.price} RON`}
                  </>
                )}
              </button>

              <p className="text-center text-[10px] text-text-muted mt-2">
                {currentLang === "ro"
                  ? "Plată securizată prin Stripe. Poți anula oricând."
                  : "Secure payment via Stripe. Cancel anytime."}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Error message for template version */}
      {error && paywallTemplate && (
        <div className="mx-4 mt-3 p-3 bg-error/10 border border-error/30 rounded-xl">
          <p className="text-error text-xs">{error}</p>
        </div>
      )}
      </div>
    </div>
  );
}
